import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header/header';
import Footer from '../components/Footer';

const QUESTIONS = [
  {
    id: 'location',
    question: 'Where are you based?',
    sub: "We'll prioritize trips within driving distance",
    icon: 'ti-location-pin',
    options: [
      { value: 'northwest', icon: 'ti-location-pin', label: 'Pacific Northwest', sub: 'WA · OR · ID · MT' },
      { value: 'west',      icon: 'ti-location-pin', label: 'West Coast',        sub: 'CA · NV · UT' },
      { value: 'southwest', icon: 'ti-location-pin', label: 'Southwest',         sub: 'AZ · NM · CO south' },
      { value: 'mountain',  icon: 'ti-location-pin', label: 'Mountain West',     sub: 'WY · CO · ID south' },
      { value: 'midwest',   icon: 'ti-location-pin', label: 'Midwest',           sub: 'IL · MN · OH · WI' },
      { value: 'east',      icon: 'ti-location-pin', label: 'East Coast',        sub: 'NY · FL · NC · MA' },
    ],
  },
  {
    id: 'duration',
    question: 'How long can you get away?',
    sub: "Be honest — weekends count too",
    icon: 'ti-calendar',
    options: [
      { value: 'weekend',  icon: 'ti-alarm-clock', label: 'Weekend',       sub: '1 – 3 days' },
      { value: 'week',     icon: 'ti-calendar',    label: 'About a week',  sub: '4 – 7 days' },
      { value: 'extended', icon: 'ti-world',       label: 'Extended trip', sub: '8+ days' },
    ],
  },
  {
    id: 'activity',
    question: 'How active are you?',
    sub: 'Pick what feels right for your fitness level',
    icon: 'ti-flag',
    options: [
      { value: 'easy',     icon: 'ti-car',       label: 'Easy & scenic', sub: 'Drives, short walks' },
      { value: 'moderate', icon: 'ti-map',        label: 'Moderate',      sub: 'Day hikes, some elevation' },
      { value: 'active',   icon: 'ti-flag',       label: 'Very active',   sub: 'Challenging trails' },
      { value: 'intense',  icon: 'ti-stats-up',   label: 'Intense',       sub: 'Backpacking, multi-day' },
    ],
  },
  {
    id: 'budget',
    question: "What's your budget per person?",
    sub: 'Include travel, lodging, and activities',
    icon: 'ti-wallet',
    options: [
      { value: 'low',  icon: 'ti-wallet',  label: 'Wallet-friendly', sub: 'Under $500' },
      { value: 'mid',  icon: 'ti-money',   label: 'Mid-range',       sub: '$500 – $1,500' },
      { value: 'high', icon: 'ti-crown',   label: 'Go all out',      sub: '$1,500+' },
    ],
  },
  {
    id: 'group',
    question: "Who's coming with you?",
    sub: 'Group size affects logistics and options',
    icon: 'ti-user',
    options: [
      { value: 'solo',   icon: 'ti-user',     label: 'Just me',      sub: 'Solo adventure' },
      { value: 'couple', icon: 'ti-heart',    label: 'Two of us',    sub: 'Couples trip' },
      { value: 'small',  icon: 'ti-id-badge', label: 'Small group',  sub: '3 – 5 people' },
      { value: 'large',  icon: 'ti-briefcase',label: 'Large group',  sub: '6+ people' },
    ],
  },
  {
    id: 'kids',
    question: 'Any little adventurers?',
    sub: 'Helps us filter family-friendly routes',
    icon: 'ti-star',
    options: [
      { value: 'no',    icon: 'ti-lock',     label: 'No kids',    sub: 'Adults only' },
      { value: 'young', icon: 'ti-star',     label: 'Young kids', sub: 'Under 12' },
      { value: 'teens', icon: 'ti-face-smile', label: 'Teenagers',  sub: '12 – 17' },
    ],
  },
];

const ITINERARIES = [
  {
    id: 'chain-lakes-loop',
    title: 'Chain Lakes Loop Trail',
    region: 'Pacific Northwest · Washington',
    coverImage: '/img/chain-lakes-loop/chain-lakes-loop.webp',
    excerpt: 'Alpine loop through wildflower meadows, turquoise lakes, and jaw-dropping volcano views near Mount Baker.',
    tags: ['Hiking', 'Day trip', 'Alpine'],
    nearRegions: ['northwest', 'west', 'mountain'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'glacier-national-park-memorial-day',
    title: 'Glacier National Park — Memorial Day',
    region: 'Pacific Northwest · Montana',
    coverImage: '/img/glacier-national-park-memorial-day-weekend.png',
    excerpt: 'Fewer crowds, car-free roads, and stunning early-season scenery at one of America\'s most breathtaking parks.',
    tags: ['Road trip', 'Scenic', 'Wildlife'],
    nearRegions: ['northwest', 'mountain', 'west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'utah-national-parks-road-trip',
    title: 'Utah National Parks Loop',
    region: 'American West · Utah',
    coverImage: '/img/utah-national-parks-road-trip/angels-landing-trail.png',
    excerpt: 'Zion, Bryce Canyon, Arches, and Canyonlands in five days — the ultimate American road trip.',
    tags: ['Road trip', 'National Parks', 'Photography'],
    nearRegions: ['west', 'southwest', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'kayak-colorado-river',
    title: '2-Day Colorado River Kayaking',
    region: 'American Southwest · Arizona',
    coverImage: '/img/kayak-colorado-river/two-day-kayak-rental.png',
    excerpt: 'Lee\'s Ferry, Antelope Canyon, and Horseshoe Bend — the Southwest\'s most spectacular two-day paddle.',
    tags: ['Water', 'Kayaking', 'Desert'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'things-to-do-in-vegas-besides-party',
    title: 'Las Vegas Beyond the Strip',
    region: 'American West · Nevada',
    coverImage: '/img/things-to-do-in-las-vegas-besides-party.webp',
    excerpt: 'Red Rock Canyon, Valley of Fire, and Lake Mead — Vegas has a wild outdoor side most tourists never see.',
    tags: ['Outdoor', 'Easy', 'Family'],
    nearRegions: ['west', 'southwest', 'mountain'],
    budget: ['low', 'mid', 'high'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate'],
  },
  {
    id: 'olympic-peninsula-loop',
    title: 'Olympic Peninsula Loop',
    region: 'Pacific Northwest · Washington',
    coverImage: '/img/olympic-peninsula-loop.webp',
    excerpt: 'Rain forest, glaciers, and ocean beaches in one epic 3-day loop from Seattle.',
    tags: ['Road trip', 'Rain forest', 'Ocean'],
    nearRegions: ['northwest', 'west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'grand-canyon-south-rim',
    title: 'Grand Canyon South Rim',
    region: 'American Southwest · Arizona',
    coverImage: '/img/grand-canyon-south-rim.webp',
    excerpt: 'Bright Angel Trail, Mather Point, and Desert View — the most iconic hike in America in three days.',
    tags: ['Hiking', 'Iconic', 'Desert'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'sedona-hiking',
    title: 'Sedona Red Rock Adventures',
    region: 'American Southwest · Arizona',
    coverImage: '/img/sedona-red-rock-adventures.webp',
    excerpt: 'Cathedral Rock, Devil\'s Bridge, Courthouse Butte, and Slide Rock — the best of Arizona\'s red rock country.',
    tags: ['Hiking', 'Scenic', 'Photography'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'yellowstone-grand-teton',
    title: 'Yellowstone & Grand Teton',
    region: 'Mountain West · Wyoming',
    coverImage: '/img/yellowstone-national-park.webp',
    excerpt: 'Old Faithful, Grand Prismatic Spring, and the Teton Range in five unforgettable days.',
    tags: ['Road trip', 'Wildlife', 'Geothermal'],
    nearRegions: ['mountain', 'northwest', 'west'],
    budget: ['mid', 'high'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'great-smoky-mountains-national-park',
    title: 'Great Smoky Mountains',
    region: 'East Coast · Tennessee/NC',
    coverImage: '/img/great-smoky-mountains-national-park.webp',
    excerpt: 'The most visited national park in the US — old-growth forests, black bears, and 800 miles of trails. No entry fee.',
    tags: ['National Parks', 'Wildlife', 'Free'],
    nearRegions: ['east'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'acadia-national-park',
    title: 'Acadia National Park',
    region: 'East Coast · Maine',
    coverImage: '/img/acadia-national-park.webp',
    excerpt: 'Granite cliffs, sunrise on Cadillac Mountain, and the famous Jordan Pond House popovers on the Maine coast.',
    tags: ['National Parks', 'Coastal', 'Hiking'],
    nearRegions: ['east'],
    budget: ['mid', 'high'],
    group: ['solo', 'couple', 'small'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'blue-ridge-parkway',
    title: 'Blue Ridge Parkway',
    region: 'East Coast · Virginia/NC',
    coverImage: '/img/blue-ridge-parkway.webp',
    excerpt: "America's longest linear national park — 469 miles of Appalachian highway with no trucks, no billboards.",
    tags: ['Road trip', 'Scenic', 'Fall Foliage'],
    nearRegions: ['east'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['week', 'extended'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate'],
  },
  {
    id: 'rocky-mountain-national-park',
    title: 'Rocky Mountain National Park',
    region: 'Mountain West · Colorado',
    coverImage: '/img/rocky-mountain-national-park.webp',
    excerpt: 'Trail Ridge Road crosses 12,000-foot alpine tundra — Bear Lake, elk rut in October, and alpine hikes above the clouds.',
    tags: ['National Parks', 'Alpine', 'Wildlife'],
    nearRegions: ['mountain', 'southwest', 'west'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active', 'intense'],
  },
  {
    id: 'boundary-waters-canoe-area',
    title: 'Boundary Waters Canoe Area',
    region: 'Midwest · Minnesota',
    coverImage: '/img/boundary-waters-canoe-area.webp',
    excerpt: "1 million acres of Minnesota wilderness — 1,200 lakes connected by portage trails, no motors, no roads, no cell service.",
    tags: ['Wilderness', 'Canoeing', 'Fishing'],
    nearRegions: ['midwest', 'northwest'],
    budget: ['mid', 'high'],
    group: ['solo', 'couple', 'small'],
    duration: ['week', 'extended'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'zion-national-park',
    title: 'Zion National Park',
    region: 'American Southwest · Utah',
    coverImage: '/img/zion-national-park.webp',
    excerpt: "Red sandstone cliffs, slot canyons, Angels Landing, and The Narrows — Utah's most dramatic park.",
    tags: ['National Parks', 'Hiking', 'Slot Canyons'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'teens'],
    activity: ['moderate', 'active', 'intense'],
  },
  {
    id: 'banff-national-park',
    title: 'Banff National Park',
    region: 'Canada · Alberta',
    coverImage: '/img/banff-national-park.webp',
    excerpt: "Moraine Lake, the Icefields Parkway, and the Columbia Icefield — one of the world's great road trips.",
    tags: ['Canada', 'Road trip', 'Glaciers'],
    nearRegions: ['northwest', 'mountain'],
    budget: ['mid', 'high'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['week', 'extended'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'shenandoah-national-park',
    title: 'Shenandoah National Park',
    region: 'East Coast · Virginia',
    coverImage: '/img/shenandoah-national-park.webp',
    excerpt: "Skyline Drive overlooks and Old Rag's legendary rock scramble — the Blue Ridge Mountains 75 miles from DC.",
    tags: ['National Parks', 'Hiking', 'Fall Foliage'],
    nearRegions: ['east'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'north-cascades-backpacking',
    title: 'North Cascades Backpacking',
    region: 'Pacific Northwest · Washington',
    coverImage: '/img/north-cascades-backpacking.webp',
    excerpt: 'Maple Pass wildflowers, jagged peaks, and true wilderness solitude in the most remote corner of the Cascades.',
    tags: ['Backpacking', 'Alpine', 'Wildflowers'],
    nearRegions: ['northwest', 'west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['week', 'extended'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'crater-lake-rim-trail',
    title: 'Crater Lake Rim Trail',
    region: 'Pacific Northwest · Oregon',
    coverImage: '/img/crater-lake-rim-trail.webp',
    excerpt: "The deepest lake in the US ringed by a 33-mile trail — brilliant blue water, volcanic cliffs, and Wizard Island.",
    tags: ['Hiking', 'Scenic', 'Volcanic'],
    nearRegions: ['northwest', 'west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'lassen-volcanic-day-hikes',
    title: 'Lassen Volcanic Day Hikes',
    region: 'Pacific Northwest · California',
    coverImage: '/img/lassen-volcanic-day-hikes.webp',
    excerpt: 'Boiling mud pots, steaming fumaroles, and alpine lakes in one compact active volcano park.',
    tags: ['Hiking', 'Volcanic', 'Day hikes'],
    nearRegions: ['west', 'northwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'grand-teton-day-hikes',
    title: 'Grand Teton Day Hikes',
    region: 'Mountain West · Wyoming',
    coverImage: '/img/grand-teton-day-hikes.webp',
    excerpt: 'Cascade Canyon, Jenny Lake, and Taggart Lake — the Tetons at their most accessible and dramatic.',
    tags: ['Hiking', 'National Parks', 'Alpine'],
    nearRegions: ['mountain', 'northwest', 'west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'john-muir-trail-section',
    title: 'John Muir Trail: Southern Section',
    region: 'American West · California',
    coverImage: '/img/john-muir-trail-section.webp',
    excerpt: 'Seven high passes, Rae Lakes, and a finish on the highest peak in the contiguous US.',
    tags: ['Backpacking', 'Sierra Nevada', 'Epic'],
    nearRegions: ['west', 'southwest'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['extended'],
    kids: ['no', 'teens'],
    activity: ['intense'],
  },
  {
    id: 'kings-canyon-backpacking',
    title: 'Kings Canyon: Rae Lakes Loop',
    region: 'American West · California',
    coverImage: '/img/kings-canyon-backpacking.webp',
    excerpt: 'A Sierra Nevada classic — turquoise Rae Lakes, towering granite walls, and the deepest canyon in the US.',
    tags: ['Backpacking', 'Sierra Nevada', 'Lakes'],
    nearRegions: ['west', 'southwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['week'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'mount-whitney-trail',
    title: 'Mount Whitney Trail',
    region: 'American West · California',
    coverImage: '/img/mount-whitney-trail.webp',
    excerpt: 'Summit the highest peak in the contiguous US at 14,505 ft — 22 miles round trip from Whitney Portal.',
    tags: ['Hiking', 'Summit', 'Challenge'],
    nearRegions: ['west', 'southwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['intense'],
  },
  {
    id: 'big-sur-hiking',
    title: 'Big Sur Coastal Trails',
    region: 'American West · California',
    coverImage: '/img/big-sur-hiking.webp',
    excerpt: 'McWay Falls, redwood canyons, and cliff-top trails along the most dramatic stretch of the California coast.',
    tags: ['Hiking', 'Coastal', 'Scenic'],
    nearRegions: ['west'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate', 'active'],
  },
  {
    id: 'death-valley-winter-hiking',
    title: 'Death Valley Winter Hiking',
    region: 'American West · California',
    coverImage: '/img/death-valley-winter-hiking.webp',
    excerpt: 'Mosaic Canyon, Badwater Basin salt flats, and Mesquite Flat Dunes — the desert at its most spectacular in winter.',
    tags: ['Desert', 'Hiking', 'Winter'],
    nearRegions: ['west', 'southwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['easy', 'moderate'],
  },
  {
    id: 'zion-national-park-itinerary',
    title: 'Zion National Park: 3-Day Itinerary',
    region: 'American Southwest · Utah',
    coverImage: '/img/zion-national-park.webp',
    excerpt: "Angels Landing, The Narrows, and Emerald Pools — the full Zion experience in three days.",
    tags: ['National Parks', 'Hiking', 'Slot Canyons'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['moderate', 'active', 'intense'],
  },
  {
    id: 'bryce-canyon-itinerary',
    title: 'Bryce Canyon: 2-Day Itinerary',
    region: 'American Southwest · Utah',
    coverImage: '/img/bryce-canyon-itinerary.webp',
    excerpt: 'Navajo Loop, Queens Garden Trail, and Fairyland — the most surreal hoodoo landscape on earth.',
    tags: ['National Parks', 'Hiking', 'Hoodoos'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'capitol-reef-itinerary',
    title: 'Capitol Reef: 2-Day Itinerary',
    region: 'American Southwest · Utah',
    coverImage: '/img/desert-hiking-tips.webp',
    excerpt: "Hickman Bridge, Cassidy Arch, and a historic fruit orchard — Utah's most underrated national park.",
    tags: ['National Parks', 'Hiking', 'Desert'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'canyonlands-needles-backpacking',
    title: 'Canyonlands: Needles District',
    region: 'American Southwest · Utah',
    coverImage: '/img/canyonlands-needles-backpacking.webp',
    excerpt: 'Chesler Park Loop and the Joint Trail — a remote canyon labyrinth with zero crowds.',
    tags: ['Backpacking', 'Desert', 'Slot Canyons'],
    nearRegions: ['southwest', 'west', 'mountain'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['weekend', 'week'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'guadalupe-mountains-hiking',
    title: 'Guadalupe Mountains Hiking',
    region: 'American Southwest · Texas',
    coverImage: '/img/desert-hiking-tips.webp',
    excerpt: "Texas's highest peak and the ancient Permian reef — one of the Southwest's most overlooked wilderness gems.",
    tags: ['Hiking', 'Desert', 'Texas'],
    nearRegions: ['southwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend'],
    kids: ['no', 'teens'],
    activity: ['moderate', 'active'],
  },
  {
    id: 'appalachian-trail-shenandoah',
    title: 'Appalachian Trail: Shenandoah',
    region: 'East Coast · Virginia',
    coverImage: '/img/appalachian-trail-through-shenandoah.webp',
    excerpt: '101 miles of ridge-walking with lodge-to-lodge hiking, stunning fall color, and Skyline Drive views.',
    tags: ['Backpacking', 'Appalachian', 'Fall Foliage'],
    nearRegions: ['east'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['week', 'extended'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'adirondack-high-peaks',
    title: 'Adirondack High Peaks',
    region: 'East Coast · New York',
    coverImage: '/img/adirondack-high-peaks.webp',
    excerpt: 'Hiking the 46ers — wild summits and old-growth forest in the largest park east of the Rockies.',
    tags: ['Hiking', 'Challenge', 'Northeast'],
    nearRegions: ['east'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['weekend', 'week'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'isle-royale-backpacking',
    title: 'Isle Royale: Greenstone Ridge',
    region: 'Midwest · Michigan',
    coverImage: '/img/isle-royale-backpacking.webp',
    excerpt: 'A car-free island wilderness in Lake Superior — wolves, moose, and 40 miles of ridge-top solitude.',
    tags: ['Backpacking', 'Wilderness', 'Island'],
    nearRegions: ['midwest'],
    budget: ['mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['week'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'pictured-rocks-backpacking',
    title: 'Pictured Rocks Backpacking',
    region: 'Midwest · Michigan',
    coverImage: '/img/pictured-rocks-backpacking.webp',
    excerpt: '42 miles of painted sandstone cliffs, waterfalls, and pristine Lake Superior beaches in the Upper Peninsula.',
    tags: ['Backpacking', 'Coastal', 'Midwest'],
    nearRegions: ['midwest'],
    budget: ['low', 'mid'],
    group: ['solo', 'couple', 'small'],
    duration: ['week'],
    kids: ['no', 'teens'],
    activity: ['active', 'intense'],
  },
  {
    id: 'haleakala-sunrise-hike',
    title: 'Haleakalā Sunrise & Crater Hike',
    region: 'Hawaii · Maui',
    coverImage: '/img/haleakala-sunrise-hike.webp',
    excerpt: "Watch sunrise above the clouds from Maui's 10,000-ft volcanic summit, then descend into the alien crater.",
    tags: ['Hiking', 'Volcano', 'Hawaii'],
    nearRegions: ['west'],
    budget: ['mid', 'high'],
    group: ['solo', 'couple', 'small', 'large'],
    duration: ['weekend', 'week'],
    kids: ['no', 'young', 'teens'],
    activity: ['moderate', 'active'],
  },
];

// Map rough lat/lon to a user region value
function coordsToRegion(lat, lon) {
  if (lat > 42 && lon < -110) return 'northwest';
  if (lat >= 32 && lat <= 42 && lon >= -124 && lon <= -114) return 'west';
  if (lat < 38 && lon >= -115 && lon <= -103) return 'southwest';
  if (lat >= 36 && lat <= 48 && lon >= -115 && lon <= -104) return 'mountain';
  if (lon > -90) return 'east';
  return 'midwest';
}

const FILTER_KEYS = ['location', 'budget', 'group', 'duration', 'kids', 'activity'];

function scoreItinerary(it, answers) {
  let score = 0;
  FILTER_KEYS.forEach(key => {
    if (!answers[key]) return;
    if (key === 'location') {
      if (it.nearRegions && it.nearRegions.includes(answers[key])) score += 1;
    } else {
      if (it[key] && it[key].includes(answers[key])) score += 1;
    }
  });
  return score;
}

export default class TripFinderPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0,
      answers: {},
      animating: false,
      direction: 'forward',
      detectingLocation: false,
    };
  }

  componentDidMount() {
    document.title = 'Find My Perfect Trip | ExplorOFF';
    window.scrollTo(0, 0);
    window.addEventListener('popstate', this.handlePopState);

    const params = new URLSearchParams(window.location.search);
    let initialStep = 0;
    let initialAnswers = {};

    if (params.get('start') === '1') {
      initialStep = 1;
    } else {
      const preAnswers = {};
      if (params.get('location')) preAnswers.location = params.get('location');
      if (params.get('activity')) preAnswers.activity = params.get('activity');
      if (params.get('duration')) preAnswers.duration = params.get('duration');
      if (Object.keys(preAnswers).length > 0) {
        const firstUnanswered = QUESTIONS.findIndex(q => !preAnswers[q.id]);
        initialStep = firstUnanswered === -1 ? QUESTIONS.length + 1 : firstUnanswered + 1;
        initialAnswers = preAnswers;
      }
    }

    window.history.replaceState({ step: initialStep, answers: initialAnswers }, '');
    this.setState({ step: initialStep, answers: initialAnswers });
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  handlePopState = (e) => {
    if (e.state && typeof e.state.step === 'number') {
      this.setState({ step: e.state.step, answers: e.state.answers || {}, direction: 'back' });
      window.scrollTo(0, 0);
    }
  };

  handleStart = () => {
    const newStep = 1;
    window.history.pushState({ step: newStep, answers: {} }, '');
    this.setState({ step: newStep, direction: 'forward' });
  };

  handleDetectLocation = () => {
    if (!navigator.geolocation) {
      const newStep = 1;
      window.history.pushState({ step: newStep, answers: {} }, '');
      this.setState({ step: newStep, direction: 'forward' });
      return;
    }
    this.setState({ detectingLocation: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const region = coordsToRegion(pos.coords.latitude, pos.coords.longitude);
        const newAnswers = { ...this.state.answers, location: region };
        const newStep = 2;
        window.history.pushState({ step: newStep, answers: newAnswers }, '');
        this.setState({
          detectingLocation: false,
          step: newStep,
          answers: newAnswers,
          direction: 'forward',
        });
        window.scrollTo(0, 0);
      },
      () => {
        const newStep = 1;
        window.history.pushState({ step: newStep, answers: {} }, '');
        this.setState({ detectingLocation: false, step: newStep, direction: 'forward' });
      },
      { timeout: 5000 }
    );
  };

  handleAnswer = (questionId, value) => {
    const { step, answers } = this.state;
    const newAnswers = { ...answers, [questionId]: value };
    const newStep = step + 1;
    this.setState({ animating: true, direction: 'forward' }, () => {
      setTimeout(() => {
        window.history.pushState({ step: newStep, answers: newAnswers }, '');
        this.setState({ step: newStep, answers: newAnswers, animating: false });
        window.scrollTo(0, 0);
      }, 260);
    });
  };

  handleBack = () => {
    window.history.back();
  };

  handleReset = () => {
    window.history.pushState({ step: 0, answers: {} }, '');
    this.setState({ step: 0, answers: {}, animating: false, direction: 'back' });
    window.scrollTo(0, 0);
  };

  renderIntro() {
    const { detectingLocation } = this.state;
    return (
      <div className="tfinder-intro">
        <div className="container">
          <div className="tfinder-intro-inner">
            <p className="tfinder-intro-eyebrow">
              <i className="ti-map-alt" /> Trip Finder
            </p>
            <h1 className="tfinder-intro-title">Find Your Perfect Adventure</h1>
            <p className="tfinder-intro-sub">
              Answer 6 quick questions and we'll match you with itineraries built for exactly your trip.
            </p>

            <div className="tfinder-intro-actions">
              <button
                className="tfinder-start-btn tfinder-start-btn--primary"
                onClick={this.handleDetectLocation}
                disabled={detectingLocation}
              >
                {detectingLocation
                  ? <><i className="ti-reload" /> Detecting…</>
                  : <><i className="ti-location-pin" /> Use my location</>}
              </button>
            </div>

            <div className="tfinder-intro-steps">
              {[
                { icon: 'ti-location-pin', label: 'Location' },
                { icon: 'ti-calendar',     label: 'Duration' },
                { icon: 'ti-wallet',       label: 'Budget' },
                { icon: 'ti-user',         label: 'Group' },
                { icon: 'ti-star',         label: 'Kids' },
                { icon: 'ti-flag',         label: 'Activity' },
              ].map((s, i) => (
                <div key={i} className="tfinder-intro-step">
                  <i className={s.icon} />
                  <span>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderQuestion() {
    const { step, answers, animating, direction } = this.state;
    const q = QUESTIONS[step - 1];
    const total = QUESTIONS.length;

    return (
      <div className={`tfinder-q-screen${animating ? ` tfinder-q-exit-${direction}` : ''}`}>
        <div className="tfinder-topbar">
          <div className="container">
            <div className="tfinder-topbar-inner">
              <button className="tfinder-back-btn" onClick={this.handleBack} aria-label="Back">
                <i className="ti-arrow-left" /> Back
              </button>
              <div className="tfinder-dots">
                {QUESTIONS.map((_, i) => (
                  <span
                    key={i}
                    className={`tfinder-dot${i < step - 1 ? ' done' : ''}${i === step - 1 ? ' active' : ''}`}
                  />
                ))}
              </div>
              <span className="tfinder-topbar-count">{step} of {total}</span>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="tfinder-q-body">
            <div className="tfinder-q-icon"><i className={q.icon} /></div>
            <h2 className="tfinder-q-title">{q.question}</h2>
            <p className="tfinder-q-sub">{q.sub}</p>

            <div className="tfinder-options-list">
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  className={`tfinder-opt${answers[q.id] === opt.value ? ' tfinder-opt--selected' : ''}`}
                  onClick={() => this.handleAnswer(q.id, opt.value)}
                >
                  <span className="tfinder-opt-icon"><i className={opt.icon} /></span>
                  <span className="tfinder-opt-text">
                    <span className="tfinder-opt-label">{opt.label}</span>
                    <span className="tfinder-opt-sub">{opt.sub}</span>
                  </span>
                  <i className="ti-check tfinder-opt-check" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderResults() {
    const { answers } = this.state;
    const maxScore = FILTER_KEYS.length;

    const scored = ITINERARIES.map(it => ({
      ...it,
      score: scoreItinerary(it, answers),
    })).sort((a, b) => b.score - a.score);

    const great  = scored.slice(0, 3);
    const others = scored.slice(3);

    const locationQ = QUESTIONS[0];
    const locationOpt = answers.location
      ? locationQ.options.find(o => o.value === answers.location)
      : null;

    const chips = QUESTIONS.map(q => {
      const opt = q.options.find(o => o.value === answers[q.id]);
      return opt ? { icon: opt.icon, label: opt.label } : null;
    }).filter(Boolean);

    return (
      <div className="tfinder-results-hero">
        <div className="container">
          <div className="tfinder-rh-inner">
            <p className="tfinder-rh-eyebrow">
              <i className="ti-check" />&nbsp;
              {locationOpt ? `Trips near ${locationOpt.label}` : 'Your matches'}
            </p>
            <h2 className="tfinder-rh-title">Your top 3 matches</h2>
            <div className="tfinder-chips">
              {chips.map((c, i) => (
                <span key={i} className="tfinder-chip">
                  <i className={c.icon} /> {c.label}
                </span>
              ))}
            </div>
            <button className="tfinder-restart-btn" onClick={this.handleReset}>
              <i className="ti-reload" /> Start over
            </button>
          </div>
        </div>

        <div className="container">
          <div className="tfinder-results-body">
            {great.length > 0 && (
              <div className="tfinder-results-section">
                <div className="tfinder-matches-grid">
                  {great.map(it => (
                    <a key={it.id} href={`/itineraries/${it.id}`} className="tfinder-match-card">
                      <div className="tfinder-match-img-wrap">
                        <img src={it.coverImage} alt={it.title} className="tfinder-match-img" loading="lazy" />
                        <div className={`tfinder-match-badge${it.score === maxScore ? ' tfinder-match-badge--perfect' : ''}`}>
                          <i className={it.score === maxScore ? 'ti-star' : 'ti-check'} />
                          {it.score === maxScore ? ' Perfect match' : ' Great match'}
                        </div>
                      </div>
                      <div className="tfinder-match-body">
                        <span className="tfinder-match-region">
                          <i className="ti-location-pin" /> {it.region}
                        </span>
                        <h3 className="tfinder-match-title">{it.title}</h3>
                        <p className="tfinder-match-excerpt">{it.excerpt}</p>
                        <div className="tfinder-match-tags">
                          {it.tags.map(t => <span key={t} className="tfinder-tag">{t}</span>)}
                        </div>
                        <span className="tfinder-match-cta">
                          View itinerary <i className="ti-arrow-right" />
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {others.length > 0 && (
              <div className="tfinder-results-section">
                <p className="tfinder-others-label">Also worth exploring</p>
                <div className="tfinder-others-row">
                  {others.slice(0, 6).map(it => (
                    <a key={it.id} href={`/itineraries/${it.id}`} className="tfinder-mini-card">
                      <img src={it.coverImage} alt={it.title} className="tfinder-mini-img" loading="lazy" />
                      <div className="tfinder-mini-body">
                        <span className="tfinder-mini-region">{it.region}</span>
                        <span className="tfinder-mini-title">{it.title}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.state;
    const showResults = step > QUESTIONS.length;
    const inQuiz = step > 0 && !showResults;

    return (
      <div className="tfinder-page">
        <Helmet>
          <title>Find My Perfect Trip | ExplorOFF</title>
          <meta name="description" content="Answer 6 quick questions and get personalized outdoor trip recommendations — hiking guides and road trip itineraries matched to your location, budget, and activity level." />
          <link rel="canonical" href="https://exploroff.com/find-my-trip" />
          <meta property="og:title" content="Find My Perfect Trip | ExplorOFF" />
          <meta property="og:description" content="Answer 6 quick questions and get personalized outdoor trip recommendations matched to your style." />
          <meta property="og:image" content="https://exploroff.com/img/logo-color-1x.png" />
          <meta property="og:url" content="https://exploroff.com/find-my-trip" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Find My Perfect Trip | ExplorOFF" />
          <meta name="twitter:description" content="Answer 6 quick questions and get personalized outdoor trip recommendations matched to your style." />
          <meta name="twitter:image" content="https://exploroff.com/img/logo-color-1x.png" />
        </Helmet>
        <Header />
        {step === 0 && this.renderIntro()}
        {inQuiz && this.renderQuestion()}
        {showResults && this.renderResults()}
        <Footer />
      </div>
    );
  }
}
